/**
 * §4 오디오 인입 — 폴리페이즈 다운샘플러 + anti-aliasing FIR.
 *
 * 1차 경로는 `AudioContext({sampleRate: 16000})`(브라우저 네이티브 리샘플러)다.
 * 이 모듈은 그것이 거부됐을 때의 **폴백**이다. 단순 데시메이션은 금지 —
 * 48k→16k 를 그냥 3개 중 1개 뽑으면 8kHz 위의 성분이 가청대로 접혀 들어온다
 * (에일리어싱, agy H-4).
 *
 * AudioWorklet(worklet/capture-processor.ts)에서 그대로 쓰이므로 DOM/워커 API
 * 의존이 없는 순수 함수로 유지한다 — 그래야 노드 환경에서 단위 테스트된다.
 */

export const TARGET_RATE = 16_000

/**
 * 윈도우드 sinc 저역통과 FIR 설계.
 *
 * @param cutoffNorm  차단 주파수(입력 샘플레이트 대비 정규화, 0..0.5)
 * @param taps        탭 수(홀수 — 선형 위상·정수 지연)
 */
export function designLowpass(cutoffNorm: number, taps: number): Float32Array {
  if (taps % 2 === 0) throw new Error('taps 는 홀수여야 한다(선형 위상)')
  if (!(cutoffNorm > 0 && cutoffNorm < 0.5)) throw new Error('cutoffNorm 은 0..0.5')

  const h = new Float32Array(taps)
  const mid = (taps - 1) / 2
  const twoPiFc = 2 * Math.PI * cutoffNorm
  let sum = 0

  for (let n = 0; n < taps; n += 1) {
    const k = n - mid
    // sinc — k=0 은 극한값 2*fc
    const sinc = k === 0 ? twoPiFc : Math.sin(twoPiFc * k) / k
    // Blackman 창 — 정지대역 감쇠 ~74dB (데시메이션 폴백에 충분)
    const w =
      0.42 -
      0.5 * Math.cos((2 * Math.PI * n) / (taps - 1)) +
      0.08 * Math.cos((4 * Math.PI * n) / (taps - 1))
    const v = sinc * w
    h[n] = v
    sum += v
  }

  // DC 이득 1 정규화 — 폴백 경로가 음량을 바꾸지 않게 한다.
  for (let n = 0; n < taps; n += 1) h[n] = (h[n] as number) / sum
  return h
}

/**
 * 임의 비율 다운샘플러(유리수 비율 아님 — 44.1k→16k 는 정수배가 아니다).
 *
 * 폴리페이즈 구조: 출력 샘플마다 입력 위상을 누적하고, 그 위치 주변의
 * FIR 탭만 곱해 더한다. 정수배(48k→16k=3)일 때는 정확히 3-폴리페이즈로
 * 축약된다.
 *
 * 스트리밍 안전 — 프레임 경계를 넘는 필터 상태(꼬리)를 내부에 보존한다.
 */
export class Downsampler {
  private readonly h: Float32Array
  private readonly ratio: number
  /**
   * FIR 히스토리 — **아직 소비되지 않은 꼬리 전체**다(길이 가변).
   *
   * 예전에는 이걸 "마지막 (taps-1) 샘플" 로 고정했다. 정수비(48k→16k=3)
   * 에서는 꼬리가 taps-1 을 넘지 않아 우연히 맞았지만, 44.1k→16k 를
   * AudioWorklet 렌더 쿼텀(128 샘플)으로 흘려 넣으면 꼬리가 129 가 되는
   * 블록이 세 번에 한 번꼴로 나온다(§4 폴백 사고 기록):
   *   - 꼬리 앞머리 1 샘플이 조용히 잘려 나갔고,
   *   - phase 는 그만큼 보정되지 않아 위상이 한 샘플씩 미끄러졌다.
   * 그 미끄러짐이 누적돼 1kHz 정현파 진폭이 1.0 → 0.12(약 -18dB)로
   * 무너졌다. 서버 VAD 바닥이 -55dBFS 라 정상 발화가 게이트 아래로
   * 떨어지는데, 클라이언트 레벨 미터는 리샘플 **전** 신호를 읽으므로
   * 화면은 초록색인 채 아무것도 통역되지 않았다 — 전형적인 조용한 유실.
   * 그래서 꼬리는 자르지 않고 통째로 보존한다.
   */
  private history: Float32Array
  /** 다음 출력 샘플의 입력 위치(소수 — history 좌표계 기준) */
  private phase = 0

  constructor(inputRate: number, outputRate: number = TARGET_RATE, taps = 129) {
    if (inputRate < outputRate) {
      throw new Error(`업샘플링 미지원: ${inputRate} < ${outputRate}`)
    }
    this.ratio = inputRate / outputRate
    // 나이퀴스트의 90% 지점에서 차단 — 전이대역을 남겨 통과대역 리플을 피한다.
    const cutoff = 0.5 / this.ratio
    this.h = designLowpass(cutoff * 0.9, taps)
    this.history = new Float32Array(taps - 1)
  }

  get decimation(): number {
    return this.ratio
  }

  /**
   * 입력 블록을 소비해 16k 샘플을 낸다.
   * 남은 꼬리는 다음 호출로 이월되므로 연속 호출 시 이음매가 없다.
   */
  process(input: Float32Array): Float32Array {
    const taps = this.h.length
    const hist = this.history.length

    // [history | input] 을 하나의 연속 버퍼로 본다.
    const buf = new Float32Array(hist + input.length)
    buf.set(this.history, 0)
    buf.set(input, hist)

    // 필터 중심이 buf 안에 완전히 들어오는 출력만 낸다.
    const out: number[] = []
    let phase = this.phase
    // 필터 중심 위치 = phase + hist (history 만큼 시프트된 좌표계)
    while (phase + taps <= buf.length) {
      const start = Math.floor(phase)
      let acc = 0
      for (let k = 0; k < taps; k += 1) {
        acc += (buf[start + k] as number) * (this.h[k] as number)
      }
      out.push(acc)
      phase += this.ratio
    }

    // 소비한 만큼 앞을 버리고 **꼬리는 통째로** 보존한다.
    //
    // 여기서 자르면 안 된다: 꼬리는 정의상 "다음 출력이 아직 읽어야 하는
    // 샘플" 이다. 길이가 taps-1 을 넘는다고 앞머리를 버리면 다음 출력이
    // 참조할 샘플이 사라지고, phase 는 그 시프트를 표현할 수 없다
    // (음수가 돼야 하므로) — 조용한 유실 + 위상 미끄러짐이 된다.
    const consumed = Math.floor(phase)
    const keepFrom = Math.max(0, consumed)
    const tail = buf.subarray(keepFrom)

    // 히스토리는 최소 taps-1 을 확보한다(짧으면 앞을 0 으로 채워 뒤쪽 정렬).
    // 그래야 초기 구간에도 필터 중심이 버퍼 안에 들어온다.
    const pad = Math.max(0, taps - 1 - tail.length)
    this.history = new Float32Array(pad + tail.length)
    this.history.set(tail, pad)

    // 좌표계 변환: 옛 buf 의 위치 p 는 새 buf 에서 p - keepFrom + pad 다.
    this.phase = phase - keepFrom + pad

    return Float32Array.from(out)
  }

  reset(): void {
    this.history = new Float32Array(this.h.length - 1)
    this.phase = 0
  }
}

/** float32 [-1,1] → PCM16 LE. 클리핑은 포화(wrap 금지). */
export function floatToPcm16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length)
  for (let i = 0; i < input.length; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i] as number))
    // 음/양 비대칭 스케일 — -1 → -32768, +1 → 32767
    out[i] = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff)
  }
  return out
}


/* ── §4 수음 레벨 계측 ───────────────────────────────────────────────────
 *
 * 서버 VAD 는 에너지 바닥 아래를 발화로 보지 않는다(§5). 마이크 입력이 그
 * 아래면 **아무리 말해도 발화가 열리지 않는다** — 사용자에게는 "통역이 안
 * 된다"로만 보인다. 레벨을 화면에 보여 주면 그 원인이 즉시 드러난다.
 *
 * **바닥값은 서버가 정본이다**(`server/vad.py` 의 `VadConfig`, `.env` 추종).
 * 서버가 `server.caps.data.vad_energy_floor_dbfs` 로 알려 주므로 클라는
 * 그 값을 파생해 쓴다 — 아래 상수는 **미수신·구 서버용 기본값**일 뿐이다.
 * 여기에 서버와 다른 값을 박으면 −45~−55 구간에서 거짓 경고가 뜬다(R4).
 */

/** 서버 VAD 에너지 바닥의 **기본값** — caps 미수신 시에만 쓴다(정본은 서버). */
export const DEFAULT_VAD_FLOOR_DBFS = -55
/** 클리핑 근접 — 이 위는 왜곡 위험. */
export const CLIP_WARN_DBFS = -3
/** 완전 무음의 표시값(-∞ 대신 쓰는 하한). */
export const SILENCE_DBFS = -100

/**
 * RMS → dBFS. 풀스케일 사인파(진폭 1.0)는 RMS 가 1/√2 이므로 -3.01dBFS 다
 * (그게 이 척도의 관례다 — 진폭 1.0 을 0dBFS 로 읽고 싶다면 파형이 아니라
 * 피크를 재야 한다).
 */
export function rmsToDbfs(rms: number): number {
  if (!(rms > 0)) return SILENCE_DBFS
  return Math.max(SILENCE_DBFS, 20 * Math.log10(rms))
}

/** 샘플 블록의 RMS(제곱평균제곱근). */
export function blockRms(samples: Float32Array): number {
  if (samples.length === 0) return 0
  let sum = 0
  for (let i = 0; i < samples.length; i += 1) {
    const v = samples[i] ?? 0
    sum += v * v
  }
  return Math.sqrt(sum / samples.length)
}

/** 샘플 블록의 dBFS — 화면 미터가 쓰는 값. */
export function blockDbfs(samples: Float32Array): number {
  return rmsToDbfs(blockRms(samples))
}

/** 블록의 **피크** 절댓값 — 클리핑 판정은 RMS 가 아니라 피크로 한다. */
export function blockPeak(samples: Float32Array): number {
  let peak = 0
  for (let i = 0; i < samples.length; i += 1) {
    const v = Math.abs(samples[i] ?? 0)
    if (v > peak) peak = v
  }
  return peak
}

export type LevelZone = 'silent' | 'low' | 'good' | 'hot'

/**
 * 레벨 구간 판정 — 색과 안내 문구의 근거.
 *
 * `low` 는 **서버 VAD 바닥 아래**라는 뜻이라, 이 상태가 지속되면 통역이
 * 시작되지 않는 이유가 바로 이것이다(진단 가치).
 *
 * 클리핑은 **피크로 판정한다**: 사인파의 RMS 는 아무리 커도 -3dBFS 를 넘지
 * 못하므로(진폭 1.0 이 곧 -3.01dBFS), RMS 문턱으로는 왜곡을 영영 못 잡는다.
 * peak 를 주지 않으면 클리핑 판정을 생략한다(없는 정보를 지어내지 않는다).
 */
export function levelZone(
  dbfs: number,
  peak?: number,
  floor: number = DEFAULT_VAD_FLOOR_DBFS,
): LevelZone {
  if (dbfs <= SILENCE_DBFS + 1) return 'silent'
  if (peak !== undefined && rmsToDbfs(peak) > CLIP_WARN_DBFS) return 'hot'
  if (dbfs < floor) return 'low'
  return 'good'
}


/* ── 레벨 경고 히스테리시스 ──────────────────────────────────────────────
 *
 * 문턱 하나로 판정하면 바닥 근처에서 경고가 **깜빡인다**(말의 자연스러운
 * 강약만으로도 초당 여러 번 오간다). 사용자에게는 그게 정보가 아니라 소음이다.
 *
 * 두 가지로 막는다:
 *   1. **진입/해제 문턱을 벌린다**(바닥 진입 / 바닥+10dB 해제) — 경계 진동 제거.
 *   2. **진입에 지속 시간을 요구한다**(2s) — 문장 사이의 짧은 숨이 경고를
 *      띄우지 않게. 해제는 즉시다: 소리가 커졌는데 경고가 남아 있으면
 *      그거야말로 거짓 정보다.
 *
 * 문턱은 **서버 바닥에서 파생한다**(`deriveLevelThresholds`) — 상수 한 쌍을
 * 박아 두면 서버 설정이 바뀔 때 화면만 틀린 말을 한다.
 */
/** 히스테리시스 폭 — 해제 문턱은 진입 문턱보다 이만큼 위다. */
export const LOW_HYSTERESIS_DB = 10
export const LOW_SUSTAIN_MS = 2_000

export interface LevelThresholds {
  /** 이 아래가 지속되면 경고 진입 */
  enter: number
  /** 이 위면 즉시 해제 */
  exit: number
}

/**
 * 서버가 알려 준 VAD 바닥에서 경고 문턱 한 쌍을 파생한다.
 *
 * 유효성: 유한수이고 −100 < floorDbfs < 0 이어야 한다. 아니면 기본값을
 * 쓴다 — 서버가 이상한 값을 주었다고 화면이 아무 소리도 안 하게 하지 않고,
 * 없는 정보를 지어내지도 않는다.
 */
export function deriveLevelThresholds(floorDbfs?: number): LevelThresholds {
  const valid =
    floorDbfs !== undefined &&
    Number.isFinite(floorDbfs) &&
    floorDbfs > -100 &&
    floorDbfs < 0
  const enter = valid ? (floorDbfs as number) : DEFAULT_VAD_FLOOR_DBFS
  return { enter, exit: enter + LOW_HYSTERESIS_DB }
}

/** caps 미수신 시의 문턱 한 쌍 — 순수 함수의 기본 인자다(가변 전역 금지). */
export const DEFAULT_LEVEL_THRESHOLDS: LevelThresholds =
  deriveLevelThresholds(undefined)

export interface LowLevelState {
  /** 현재 경고 표시 여부 */
  warn: boolean
  /** 문턱 아래로 처음 떨어진 시각(ms). 위에 있으면 null */
  since: number | null
}

export const initialLowLevelState: LowLevelState = { warn: false, since: null }

/**
 * 저입력 경고 상태를 갱신한다(순수 함수 — 테스트가 여기를 겨눈다).
 *
 * `active=false`(캡처 중지·뮤트)면 경고를 끄고 타이머도 리셋한다 — 꺼진
 * 마이크에 "입력이 작습니다" 는 틀린 말이다.
 *
 * 문턱은 **인자로 받는다**. 이 모듈은 AudioWorklet 그래프에 들어가므로
 * (`worklet/capture-processor.ts`) 가변 전역을 두면 워크릿 사본과 메인
 * 스레드 사본이 갈린다 — 순수함이 여기서는 불변식이다.
 */
export function nextLowLevelState(
  prev: LowLevelState,
  dbfs: number,
  now: number,
  active: boolean,
  th: LevelThresholds = DEFAULT_LEVEL_THRESHOLDS,
): LowLevelState {
  if (!active) return initialLowLevelState

  // 해제는 즉시 — 커졌는데 경고가 남으면 거짓 정보다.
  if (dbfs > th.exit) return { warn: false, since: null }

  // 진입 문턱 위(해제 문턱과의 사이)면 **현 상태를 유지**한다(히스테리시스).
  if (dbfs > th.enter) return { ...prev }

  // 진입 문턱 아래 — 지속 시간을 채워야 경고를 켠다.
  const since = prev.since ?? now
  return { warn: prev.warn || now - since >= LOW_SUSTAIN_MS, since }
}
